import { motion } from "framer-motion";
import { ArrowUpRight, Truck } from "lucide-react";
import { SectionHead, EASE } from "./motion";
import { scrollToId } from "./SmoothScroll";

const TILES = [
  { id: "ceramic", t: "Ceramic Coatings", d: "1, 2 and 5-year coatings with Stage 1 polish and two complimentary maintenance washes.", img: "/img/polish.jpg", cls: "lg:col-span-8 lg:row-span-2", big: true },
  { id: "correction", t: "Paint Correction", d: "Single, two and multi-stage.", img: "/img/polish2.jpg", cls: "lg:col-span-4" },
  { id: "tint", t: "Window Tint", d: "Nano-ceramic film · lifetime warranty.", img: "/img/tintglass.jpg", cls: "lg:col-span-4" },
  { id: "interior", t: "Interior Detailing", d: "Refresh Clean, Showroom Ready, Ultimate Icon.", img: "/img/interior.jpg", cls: "lg:col-span-4 lg:row-span-2", tall: true },
  { id: "ppf", t: "Paint Protection Film", d: "Front packages & custom coverage.", img: "/img/buffing.jpg", cls: "lg:col-span-4" },
  { id: "marine", t: "Marine & RV", d: "Gelcoat polish, sealant, RV wash & wax.", img: "/img/boat.jpg", cls: "lg:col-span-4" },
];

const Tile = ({ tile, i }) => (
  <motion.button
    onClick={() => scrollToId("pricing")}
    data-testid={`bento-${tile.id}`}
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: i * 0.07 }}
    className={`group relative overflow-hidden text-left bg-[#0e1118] border border-white/10 min-h-[280px] ${tile.big ? "sm:min-h-[420px]" : ""} ${tile.cls}`}
  >
    <img src={tile.img} alt={tile.t} className="absolute inset-0 h-full w-full object-cover opacity-60 grayscale-[35%] transition-[transform,opacity,filter] duration-[1.2s] ease-out group-hover:scale-[1.06] group-hover:opacity-80 group-hover:grayscale-0" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-[#07080B]/50 to-transparent" />
    <div className="absolute inset-x-0 top-0 h-px bg-[#1bb4f2] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
    <span className="absolute top-5 left-5 font-mono text-xs text-slate-300 tracking-[0.2em]">0{i + 1}</span>
    <span className="absolute top-5 right-5 h-10 w-10 grid place-items-center border border-white/20 bg-black/30 backdrop-blur group-hover:bg-[#1bb4f2] group-hover:text-[#07080B] group-hover:border-[#1bb4f2] transition-colors">
      <ArrowUpRight className="h-4 w-4" />
    </span>
    <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
      <h3 className={`font-display font-black uppercase leading-[0.95] ${tile.big ? "text-3xl md:text-5xl" : "text-2xl"}`}>{tile.t}</h3>
      <p className="mt-2 text-sm text-slate-300 max-w-md">{tile.d}</p>
    </div>
  </motion.button>
);

const MobileTile = () => (
  <motion.button
    onClick={() => scrollToId("mobile")} data-testid="bento-mobile"
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: 0.4 }}
    className="group relative overflow-hidden text-left lg:col-span-8 min-h-[280px] bg-[#1bb4f2] text-[#07080B] p-6 md:p-8 flex flex-col justify-between"
  >
    <div className="flex items-start justify-between">
      <Truck className="h-9 w-9" />
      <span className="font-mono text-xs uppercase tracking-[0.25em]">On the road</span>
    </div>
    <div>
      <h3 className="font-display font-black uppercase text-3xl md:text-5xl leading-[0.92]">Mobile detailing.<br />Own water. Own power.</h3>
      <p className="mt-3 text-sm font-medium max-w-lg">From Salmon Arm to Kelowna — we roll up fully self-contained.</p>
    </div>
    <div className="absolute -right-10 -bottom-10 h-56 w-56 rounded-full border-[28px] border-[#07080B]/10 transition-transform duration-700 group-hover:scale-125" />
  </motion.button>
);

export const Bento = () => (
  <section id="services" data-testid="services-bento" className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-36">
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 md:mb-20">
      <SectionHead eyebrow="01 — Services" lines={["Every surface,", "obsessed over."]} />
      <p className="max-w-sm text-slate-300 lg:text-right">Cars, trucks, boats and motorhomes. Inside, outside and everything that needs protecting.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[260px] gap-3 md:gap-4">
      {TILES.map((t, i) => <Tile key={t.id} tile={t} i={i} />)}
      <MobileTile />
    </div>
  </section>
);
